import { parseInventoryQuantity } from './inventoryScannerModel.js';

export const INVENTORY_MODE_SET_ID = 'INVENTORY-MODE';

export const PHYSICAL_INVENTORY_TARGETS = [
  ...[1, 2, 3, 4, 5, 6].map(number => ({
    id: `LM-PACK-${String(number).padStart(2, '0')}`,
    name: `Line Medic Pack ${String(number).padStart(2, '0')}`,
    type: 'pack',
    seasonalStatus: number <= 3 ? 'active' : 'oos',
  })),
  { id: 'BLS-PACK-01', name: 'Line EMT BLS Pack 01', type: 'bls', seasonalStatus: 'active' },
  ...[1, 2, 3, 4, 5, 6].map(number => ({
    id: `TRAUMA-BAG-${String(number).padStart(2, '0')}`,
    name: `Trauma Bag ${String(number).padStart(2, '0')}`,
    type: 'trauma',
    seasonalStatus: number <= 3 ? 'active' : 'oos',
  })),
  ...[1, 2, 3, 4, 5, 6].map(number => ({
    id: `O2-BAG-${String(number).padStart(2, '0')}`,
    name: `O2 Bag ${String(number).padStart(2, '0')}`,
    type: 'o2',
    seasonalStatus: number <= 3 ? 'active' : 'oos',
  })),
  ...[1, 2, 3, 4].map(number => ({
    id: `AED-${String(number).padStart(2, '0')}`,
    name: `AED ${String(number).padStart(2, '0')}`,
    type: 'aed',
    seasonalStatus: 'active',
  })),
  { id: 'LM-PACK-GREG', name: "Greg's Line Medic Pack", type: 'pack', seasonalStatus: 'active', personal: true },
  { id: 'TRAUMA-BAG-GREG', name: "Greg's Trauma Bag", type: 'trauma', seasonalStatus: 'active', personal: true },
  { id: 'O2-BAG-GREG', name: "Greg's O2 Bag", type: 'o2', seasonalStatus: 'active', personal: true },
  { id: 'CLIPBOARD-GREG', name: "Greg's Clipboard", type: 'clipboard', seasonalStatus: 'active', personal: true },
];

export function normalizeInventoryTargetId(value) {
  return String(value || '').trim().toUpperCase();
}

export function inventoryTargetType(targetId) {
  const id = normalizeInventoryTargetId(targetId);
  if (/^BLS-PACK-/.test(id)) return 'bls';
  if (/^TRAUMA-BAG-/.test(id)) return 'trauma';
  if (/^O2-BAG-/.test(id)) return 'o2';
  if (/^AED-/.test(id)) return 'aed';
  if (/^LM-PACK-/.test(id)) return 'pack';
  if (/^CLIPBOARD-/.test(id)) return 'clipboard';
  return 'all';
}

function catalogText(item) {
  return [item?.component, item?.item, item?.source, item?.notes]
    .map(value => String(value || '').trim().toLowerCase())
    .join(' ');
}

function safeId(value) {
  return String(value || '').replace(/[^A-Za-z0-9_.:-]+/g, '-');
}

function trackingModeFromText(value) {
  const text = String(value || '').trim().toLowerCase();
  if (/\btracking(?:\s+mode)?\s*:\s*(counted|bulk|count)\b/.test(text) || /\[(counted|bulk)\]/.test(text)) return 'counted';
  if (/\btracking(?:\s+mode)?\s*:\s*(unit|individual)\b/.test(text)) return 'unit';
  return '';
}

export function isInventoryModeRecord(record) {
  return normalizeInventoryTargetId(record?.setId) === INVENTORY_MODE_SET_ID;
}

export function inventoryTrackingMode(item, expirations = []) {
  const catalogId = String(item?.catalogId || '').trim();
  if (catalogId) {
    const marker = (Array.isArray(expirations) ? expirations : []).find(record =>
      isInventoryModeRecord(record) && String(record?.catalogId || '').trim() === catalogId
    );
    const markerMode = trackingModeFromText(marker?.notes);
    if (markerMode) return markerMode;
  }

  const catalogMode = trackingModeFromText(item?.notes);
  if (catalogMode) return catalogMode;

  if (catalogId) {
    const hasCountedHistory = (Array.isArray(expirations) ? expirations : []).some(record =>
      String(record?.catalogId || '').trim() === catalogId &&
      /counted\s*\/\s*bulk inventory snapshot/i.test(String(record?.notes || ''))
    );
    if (hasCountedHistory) return 'counted';
  }

  return 'unit';
}

export function catalogItemAppliesToTarget(item, targetId) {
  const type = inventoryTargetType(targetId);
  const catalogId = String(item?.catalogId || '').trim().toUpperCase();
  const component = String(item?.component || '').trim().toLowerCase();
  const text = catalogText(item);
  const general = component === 'general';
  const bls = catalogId.startsWith('BLS-') || text.includes('true north bls');
  const trauma = component.includes('trauma bag') || component.includes('red trauma');
  const oxygen = component.includes('o2') || component.includes('oxygen') || text.includes('o2 bag') || text.includes('oxygen bag');
  const aed = component.includes('aed') || text.includes('aed ') || text.includes('defibrillator') || text.includes('electrode');

  if (type === 'bls') return bls && !general;
  if (type === 'trauma') return trauma;
  if (type === 'o2') return oxygen;
  if (type === 'aed') return aed;
  if (type === 'clipboard') return false;
  if (type === 'pack') return !bls && !general && !trauma && !oxygen && !aed;
  return true;
}

export function inventoryCatalogForTarget(catalog, targetId) {
  return (Array.isArray(catalog) ? catalog : []).filter(item => catalogItemAppliesToTarget(item, targetId));
}

export function activeUnitRecords(expirations, targetId, catalogId) {
  const target = normalizeInventoryTargetId(targetId);
  const catalog = String(catalogId || '').trim();
  return (Array.isArray(expirations) ? expirations : []).filter(record =>
    normalizeInventoryTargetId(record?.setId) === target &&
    String(record?.catalogId || '').trim() === catalog &&
    !record?.returnedToLogistics &&
    parseInventoryQuantity(record?.requiredQty, 0) > 0
  );
}

export function countInventoryUnits(records) {
  return (Array.isArray(records) ? records : []).reduce(
    (sum, record) => sum + Math.max(0, parseInventoryQuantity(record?.requiredQty, 0)),
    0,
  );
}

function appendNote(existing, addition) {
  const left = String(existing || '').trim();
  const right = String(addition || '').trim();
  if (!right) return left;
  return left ? `${left} | ${right}` : right;
}

export function makeInventoryUnitRecord(target, catalogItem, options = {}) {
  const targetId = normalizeInventoryTargetId(target?.id);
  const targetName = String(target?.name || targetId).trim() || targetId;
  const catalogId = String(catalogItem?.catalogId || '').trim();
  const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  const expirationDate = options.noExpiration ? '' : String(options.expirationDate || '').trim();
  let notes = String(options.notes || '').trim();
  const barcode = String(options.barcode || '').trim();
  if (barcode) notes = appendNote(notes, `Barcode ${barcode}`);
  notes = appendNote(notes, 'Individual physical unit');

  return {
    recordId: `${targetId}-${catalogId}-UNIT-${stamp}`.replace(/[^A-Za-z0-9_.:-]+/g, '-'),
    setId: targetId,
    setName: targetName,
    catalogId,
    component: String(catalogItem?.component || '').trim(),
    item: String(catalogItem?.item || '').trim(),
    requiredQty: '1',
    expirationDate,
    expirationState: options.noExpiration ? 'NA' : (expirationDate ? 'DATED' : 'UNKNOWN'),
    lotSerial: String(options.lotSerial || '').trim(),
    returnedToLogistics: false,
    returnedDate: '',
    replacementDate: String(options.replacementDate || '').trim(),
    notes,
    updatedAt: '',
  };
}

export function makeCountedInventoryRecords(target, catalogItem, activeRecords, quantity, options = {}) {
  const targetId = normalizeInventoryTargetId(target?.id);
  const targetName = String(target?.name || targetId).trim() || targetId;
  const catalogId = String(catalogItem?.catalogId || '').trim();
  const parsed = parseInventoryQuantity(quantity, -1);
  if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) throw new Error('Counted inventory quantity must be a whole number of zero or more.');

  const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  const note = String(options.notes || '').trim();
  const retired = (Array.isArray(activeRecords) ? activeRecords : []).map(record => ({
    ...record,
    requiredQty: '0',
    expirationDate: '',
    expirationState: 'NA',
    notes: appendNote(record?.notes, `Superseded by counted inventory ${stamp}`),
    updatedAt: '',
  }));

  const current = {
    recordId: `${targetId}-${catalogId}-COUNT-${stamp}`.replace(/[^A-Za-z0-9_.:-]+/g, '-'),
    setId: targetId,
    setName: targetName,
    catalogId,
    component: String(catalogItem?.component || '').trim(),
    item: String(catalogItem?.item || '').trim(),
    requiredQty: String(parsed),
    expirationDate: '',
    expirationState: 'NA',
    lotSerial: '',
    returnedToLogistics: false,
    returnedDate: '',
    replacementDate: String(options.replacementDate || '').trim(),
    notes: appendNote(note, 'Counted / bulk inventory snapshot'),
    updatedAt: '',
  };

  return [...retired, current];
}

export function makeInventoryTrackingModeRecord(catalogItem, mode) {
  const normalizedMode = mode === 'counted' ? 'counted' : 'unit';
  const catalogId = String(catalogItem?.catalogId || '').trim();
  return {
    recordId: safeId(`${INVENTORY_MODE_SET_ID}-${catalogId}`),
    setId: INVENTORY_MODE_SET_ID,
    setName: 'Inventory Tracking Modes',
    catalogId,
    component: String(catalogItem?.component || '').trim(),
    item: String(catalogItem?.item || '').trim(),
    requiredQty: '0',
    expirationDate: '',
    expirationState: 'NA',
    lotSerial: '',
    returnedToLogistics: true,
    returnedDate: '',
    replacementDate: '',
    notes: `Tracking mode: ${normalizedMode}`,
    updatedAt: '',
  };
}

export function makeInventoryTrackingModeChangeRecords(catalogItem, mode, expirations = []) {
  const normalizedMode = mode === 'counted' ? 'counted' : 'unit';
  const catalogId = String(catalogItem?.catalogId || '').trim();
  if (!catalogId) throw new Error('Catalog ID is required to change inventory tracking mode.');

  const records = [makeInventoryTrackingModeRecord(catalogItem, normalizedMode)];
  const activeForItem = (Array.isArray(expirations) ? expirations : []).filter(record =>
    !isInventoryModeRecord(record) &&
    String(record?.catalogId || '').trim() === catalogId &&
    !record?.returnedToLogistics &&
    parseInventoryQuantity(record?.requiredQty, 0) > 0
  );

  if (normalizedMode === 'counted') {
    const byTarget = new Map();
    activeForItem.forEach(record => {
      const setId = normalizeInventoryTargetId(record?.setId);
      if (!setId) return;
      if (!byTarget.has(setId)) byTarget.set(setId, []);
      byTarget.get(setId).push(record);
    });

    byTarget.forEach((targetRecords, setId) => {
      const alreadyCounted = targetRecords.length === 1 && /counted\s*\/\s*bulk inventory snapshot/i.test(String(targetRecords[0]?.notes || ''));
      if (alreadyCounted) return;
      const first = targetRecords[0] || {};
      records.push(...makeCountedInventoryRecords(
        { id: setId, name: String(first?.setName || setId).trim() || setId },
        catalogItem,
        targetRecords,
        countInventoryUnits(targetRecords),
        { notes: 'Converted automatically when item was marked counted / bulk' },
      ));
    });
    return records;
  }

  activeForItem
    .filter(record => /counted\s*\/\s*bulk inventory snapshot/i.test(String(record?.notes || '')))
    .forEach(record => {
      records.push({
        ...record,
        requiredQty: '0',
        expirationDate: '',
        expirationState: 'NA',
        notes: appendNote(record?.notes, 'Retired when tracking mode changed to individual unit'),
        updatedAt: '',
      });
    });

  return records;
}
